import { useAppDispatch, useAppSelector } from "app.hook";
import { FormikErrors, FormikProps, useFormik } from "formik";
import React, { FC, useEffect, useRef, useState } from "react";
import { shallowEqual } from "react-redux";
import { Link } from "react-router-dom";
import { CustomPhoneInput } from "shared/components/custom-input/custom-phone-input";
import {
  ACCOUNT_LOGIN_ROUTE,
  ACCOUNT_REGISTER_ROUTE,
} from "shared/constants/routes";
import useCallback from "shared/hooks/use-callback";
import { ILoginRequestModel } from "states/account/account.models";
import { createCustomerInfoAPI } from "states/customer/customer.api";
import { ICreatedCustomerModel } from "states/customer/customer.models";
import {
  loginRequest,
  selectCaptcha,
  setLoading,
  showError,
  showSuccess,
} from "states/global-value/global-value.slice";
import { IconArrowLeft } from "themes/icons";
import { PrimaryButton } from "themes/style-components/button";
import {
  FlexAuto,
  FlexContainer,
  FlexLeftContainer,
} from "themes/style-components/flex";
import {
  FormControl,
  FormError,
  FormGroup,
  FormGroupNotFlex,
} from "themes/style-components/form-group";
import { H4, H5 } from "themes/style-components/typography";
const containerId = "recaptcha";
const RegisterPage: FC = () => {
  const dispatch = useAppDispatch();
  const captcha = useAppSelector(selectCaptcha, shallowEqual);
  const [callbackQuery, callbackUrl] = useCallback("callback");
  // const [captchaResponse, setCaptchaResponse] = useState(null);
  // const successCallback = (response: any) => {
  //   setCaptchaResponse(response);
  // };

  // const expiredCallback = () => setCaptchaResponse(null);
  // useRecaptcha({
  //   containerId,
  //   successCallback,
  //   expiredCallback,
  //   size: 'normal',
  //   sitekey: captcha?.publicKey,
  // });
  const [isSuccess, setSuccess] = useState<boolean>(false);
  const formik: FormikProps<ICreatedCustomerModel> =
    useFormik<ICreatedCustomerModel>({
      initialValues: {
        email: "",
        phoneNumber: "",
        firstName: "",
        lastName: "",
        password: "",
        confirmPassword: "",
      },
      validateOnChange: true,
      validate: (values) => {
        let errors: FormikErrors<ICreatedCustomerModel> = {};
        if (values.email === "") {
          errors.email = "Please enter email";
        } else if (
          !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)
        ) {
          errors.email = "Invalid email address";
        }
        if (values.phoneNumber === "") {
          errors.phoneNumber = "Please enter phone";
        }
        if (values.firstName === "") {
          errors.firstName = "Please enter first name";
        }
        if (values.lastName === "") {
          errors.lastName = "Please enter last name";
        }
        if (values.password === "") {
          errors.password = "Please enter password";
        } else if (values.password.length < 6) {
          errors.password = "At least 6 characters";
        }
        if (values.confirmPassword === "") {
          errors.confirmPassword = "Please enter confirm password";
        } else if (values.password !== values.confirmPassword) {
          errors.confirmPassword = "Password must match";
        }
        return errors;
      },
      onSubmit: async (values) => {
        // if(captchaResponse === ""){
        //   dispatch(showError("Please select captcha"));
        //   return;
        // }
        dispatch(setLoading(true));
        try {
          values.phoneNumber = values.phoneNumber
            .replace(/\)/g, "")
            .replace(/\(/g, "")
            .replace(/-/g, "")
            .replace(/ /g, "");
          await createCustomerInfoAPI(values);
          dispatch(setLoading(false));
          dispatch(
            showSuccess("Your account has been registered successfully")
          );
          setSuccess(true);
          await handleLoginUser(values.email, values.password);
        } catch (err) {
          dispatch(setLoading(false));
          dispatch(showError(err?.response?.data?.error?.message));
        }
      },
    });
  const handleLoginUser = async (userName: string, pwd: string) => {
    dispatch(setLoading(true));
    try {
      await dispatch(
        loginRequest({
          userName: userName,
          password: pwd,
        } as ILoginRequestModel)
      ).unwrap();
      if (callbackUrl !== "") {
        window.location.href = callbackUrl;
      }
      dispatch(setLoading(false));
    } catch (rejectedValueOrSerializedError) {
      dispatch(setLoading(false));
      dispatch(showError(rejectedValueOrSerializedError as any));
    }
  };
  useEffect(() => {
    document.title = "Register Password";
    // Add reCaptcha
  }, []);
  return (
    <>
      {isSuccess ? (
        <>
          <FlexContainer>
            <FlexLeftContainer>
              <Link to={`${ACCOUNT_LOGIN_ROUTE}${callbackQuery}`}>
                <IconArrowLeft width={15} />
              </Link>
            </FlexLeftContainer>
            <FlexAuto className="text-center">
              <H4 className="mb-8 uppercase">Successfully</H4>
            </FlexAuto>
          </FlexContainer>
          <div className="content text-center">
            <H5>{formik.values.email}</H5>
            <p>Account has been registered successfully</p>
          </div>
          <Link to={`${ACCOUNT_LOGIN_ROUTE}${callbackQuery}`}>
            <PrimaryButton className="block w-full mt-4" type="button">
              Login
            </PrimaryButton>
          </Link>
        </>
      ) : (
        <>
          <FlexContainer>
            <FlexLeftContainer>
              <Link aria-label="Click redirect to login page" to={`${ACCOUNT_LOGIN_ROUTE}${callbackQuery}`}>
                <IconArrowLeft width={15} />
              </Link>
            </FlexLeftContainer>
            <FlexAuto className="text-center">
              <H4 className="mb-8 uppercase">Register Password</H4>
            </FlexAuto>
          </FlexContainer>
          <form onSubmit={formik.handleSubmit}>
            <FormGroupNotFlex className="mb-4">
              <FormControl>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  placeholder="First Name"
                  aria-placeholder="Please enter first name"
                  onChange={formik.handleChange}
                  value={formik.values.firstName}
                />
              </FormControl>
              {formik.errors.firstName && (
                <FormError>{formik.errors.firstName}</FormError>
              )}
            </FormGroupNotFlex>
            <FormGroupNotFlex className="mb-4">
              <FormControl>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  placeholder="Last Name"
                  aria-placeholder="Please enter last name"
                  onChange={formik.handleChange}
                  value={formik.values.lastName}
                />
              </FormControl>
              {formik.errors.lastName && (
                <FormError>{formik.errors.lastName}</FormError>
              )}
            </FormGroupNotFlex>
            <FormGroupNotFlex className="mb-4">
              <FormControl>
                <CustomPhoneInput
                  id="phoneNumber"
                  name="phoneNumber"
                  aria-placeholder="Please enter pone number"
                  onChange={formik.handleChange}
                  value={formik.values.phoneNumber}
                />
              </FormControl>
              {formik.errors.phoneNumber && (
                <FormError>{formik.errors.phoneNumber}</FormError>
              )}
            </FormGroupNotFlex>
            <FormGroupNotFlex className="mb-4">
              <FormControl>
                <input
                  id="email"
                  name="email"
                  type="text"
                  placeholder="Email"
                  aria-placeholder="Please enter email address"
                  onChange={formik.handleChange}
                  value={formik.values.email}
                />
              </FormControl>
              {formik.errors.email && (
                <FormError>{formik.errors.email}</FormError>
              )}
            </FormGroupNotFlex>
            <FormGroupNotFlex className="mb-4">
              <FormControl>
                <input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Password"
                  aria-placeholder="Please enter password"
                  onChange={formik.handleChange}
                  value={formik.values.password}
                />
              </FormControl>
              {formik.errors.password && (
                <FormError>{formik.errors.password}</FormError>
              )}
            </FormGroupNotFlex>
            <FormGroupNotFlex className="mb-4">
              <FormControl>
                <input
                  id="confirm__password"
                  name="confirmPassword"
                  type="password"
                  placeholder="Confirm Password"
                  aria-placeholder="Please enter confirm password"
                  onChange={formik.handleChange}
                  value={formik.values.confirmPassword}
                />
              </FormControl>
              {formik.errors.confirmPassword && (
                <FormError>{formik.errors.confirmPassword}</FormError>
              )}
            </FormGroupNotFlex>
            {/* <div id={containerId} className="g-recaptcha"></div> */}
            <PrimaryButton className="block w-full mt-4" type="submit">
              Submit
            </PrimaryButton>
          </form>
        </>
      )}
    </>
  );
};

export default RegisterPage;
